import React, { useCallback, useEffect, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import CoContributionsModal from '~support-at-home/components/co-contributions/CoContributionsModal';

import PaymentBatchDetail from '~direct-debits/components/payment-batches/PaymentBatchDetail';
import PaymentBatchesList from '~direct-debits/components/payment-batches/PaymentBatchesList';
import RefundModal from '~direct-debits/components/payment-batches/RefundModal';
import ReverseModal from '~direct-debits/components/payment-batches/ReverseModal';
import { PaymentBatchesWizard } from '~direct-debits/components/payment-batches-wizard';

import { useCreateRefundMutation, useReversePaymentMutation } from '~direct-debits/actions/refunds';
import { PaymentBatchClient } from '~direct-debits/models';

import { setTitle } from '~main/actions/login';

interface ComponentProps {
  isActive?: boolean;
}

const PaymentBatchesContainer: React.FC<ComponentProps> = ({ isActive = true }) => {
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [refundLine, setRefundLine] = useState<PaymentBatchClient | null>(null);
  const [reverseLine, setReverseLine] = useState<PaymentBatchClient | null>(null);
  // Last failure per action — shown inline in the modal (persistent) instead of
  // a transient toast, so the operator can read it and doesn't immediately retry.
  const [refundError, setRefundError] = useState<string | null>(null);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { showToast } = useToast();
  const [createRefund, refundState] = useCreateRefundMutation();
  const [reversePayment, reverseState] = useReversePaymentMutation();

  useEffect(() => {
    if (isActive) setTitle('Direct Debits - Payment Batches');
  }, [isActive]);

  const handleSelectBatch = useCallback((batchId: string) => {
    setSelectedBatchId(batchId);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedBatchId(null);
    setSelectedClientId(null);
    // Defensive: if the user reaches handleBackToList while a deeper dialog is
    // still open (e.g. via keyboard), the CoCon modal would otherwise remain
    // mounted/open on top of the level-1 list.
    setSelectedInvoiceId(null);
    setRefundLine(null);
    setReverseLine(null);
  }, []);

  // The invoices open in a dialog stacked over the batch detail, so the row
  // icon only ever opens: picking another client swaps which client's invoices
  // that dialog shows.
  const handleViewInvoices = useCallback((clientId: number) => {
    setSelectedClientId(clientId);
  }, []);

  const handleCloseInvoices = useCallback(() => {
    setSelectedClientId(null);
    setSelectedInvoiceId(null);
  }, []);

  const handleViewInvoice = useCallback((invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
  }, []);

  const handleRefund = useCallback((row: PaymentBatchClient) => {
    setRefundError(null);
    setRefundLine(row);
  }, []);

  const handleReverse = useCallback((row: PaymentBatchClient) => {
    setReverseError(null);
    setReverseLine(row);
  }, []);

  const handleConfirmRefund = useCallback(
    async (reason: string) => {
      if (!refundLine?.payment_batch_item_id || selectedBatchId === null) return;
      setRefundError(null);
      try {
        await createRefund({
          payment_batch_item_id: refundLine.payment_batch_item_id,
          reason,
          batch_id: selectedBatchId,
        }).unwrap();
        showToast('success', `Refund submitted for ${refundLine.client_name}`);
        setRefundLine(null);
      } catch (err) {
        setRefundError(getErrorMessage(err));
      }
    },
    [createRefund, refundLine, selectedBatchId, showToast],
  );

  const handleConfirmReverse = useCallback(
    async (reason: string) => {
      if (!reverseLine?.payment_batch_item_id || selectedBatchId === null) return;
      setReverseError(null);
      try {
        await reversePayment({
          payment_batch_item_id: reverseLine.payment_batch_item_id,
          reason: reason || undefined,
          batch_id: selectedBatchId,
        }).unwrap();
        showToast('success', `Payment reversed for ${reverseLine.client_name}`);
        setReverseLine(null);
      } catch (err) {
        setReverseError(getErrorMessage(err));
      }
    },
    [reversePayment, reverseLine, selectedBatchId, showToast],
  );

  // Reactstrap controlled modal helpers — the CoCon modal expects setIsOpen
  // rather than onClose, so adapt the callback shape here.
  const setInvoiceModalOpen = useCallback((open: boolean) => {
    if (!open) setSelectedInvoiceId(null);
  }, []);

  return (
    <div data-cy="dd-payment-batches-container">
      {/*
        The list stays mounted: the batch detail opens as a wide dialog over it
        so the drill-down reads as a deeper level rather than a page swap.
      */}
      <PaymentBatchesList
        isActive={isActive}
        onSelectBatch={handleSelectBatch}
        onCreate={() => setIsCreating(true)}
      />
      {selectedBatchId !== null && (
        <PaymentBatchDetail
          batchId={selectedBatchId}
          onBack={handleBackToList}
          onViewInvoices={handleViewInvoices}
          selectedClientId={selectedClientId}
          onCloseInvoices={handleCloseInvoices}
          onViewInvoice={handleViewInvoice}
          onRefund={handleRefund}
          onReverse={handleReverse}
        />
      )}
      <RefundModal
        line={refundLine}
        batchId={selectedBatchId}
        isOpen={refundLine !== null}
        isSubmitting={refundState.isLoading}
        errorMessage={refundError}
        onCancel={() => setRefundLine(null)}
        onConfirm={handleConfirmRefund}
      />
      <ReverseModal
        line={reverseLine}
        batchId={selectedBatchId}
        isOpen={reverseLine !== null}
        isSubmitting={reverseState.isLoading}
        errorMessage={reverseError}
        onCancel={() => setReverseLine(null)}
        onConfirm={handleConfirmReverse}
      />
      {/*
        Invoice record. The existing CoCon modal already accepts an
        invoice ID via its (misleadingly named) `clientId` prop and handles its
        own data load + status transitions. Reusing it keeps the experience
        consistent with the SaH co-contributions screen and avoids duplicating
        the invoice editor here. TAV-2053 is the consolidation candidate to
        rename that prop and decouple the modal from the SaH module.
      */}
      <CoContributionsModal
        isOpen={selectedInvoiceId !== null}
        setIsOpen={setInvoiceModalOpen}
        clientId={selectedInvoiceId}
      />
      <PaymentBatchesWizard isOpen={isCreating} setIsOpen={setIsCreating} />
    </div>
  );
};

export default PaymentBatchesContainer;
